import { graphql } from "gatsby"
import React from "react"
import { Spacer } from "../atoms/layout-primitives"
import { BlogGrid } from "../atoms/layout-primitives/grid"
import ArticleCardGrid from "../organisms/article-card-grid"
import BlogCardGrid, { IBlogCard } from "../templates/blog-card-grid"
import { IContentfulComposePage } from "../types/blog.types"
import { TWebsite } from "../types/contentful.types"
import {
  contentfulComposePageToArticleCard,
  richTextObjectToString,
  truncateContent,
} from "../util/contentful-data"
import InternalRoutes from "../util/internal-routes"
import { IArticle } from "./article-card"
import ArticleRowTitle from "./article-row-title"

const DOTS_HEIGHT = "24px"

export interface IFeaturedArticles {
  __typename: "ContentfulFeaturedCards"
  title: string
  url?: string
  cards: IContentfulComposePage[]
}

interface IFeaturedArticlesProps extends Omit<IFeaturedArticles, "__typename"> {
  website: TWebsite
}

const FeaturedArticles = ({ website, ...rest }: IFeaturedArticlesProps) => {
  switch (website) {
    case "Industries":
      return <FeaturedArticles.Industry {...rest} />
    case "Blog":
    default:
      return <FeaturedArticles.Blog {...rest} />
  }
}

FeaturedArticles.Blog = ({
  title,
  url,
  cards,
}: Omit<IFeaturedArticlesProps, "website">) => {
  const articles: IArticle[] =
    cards?.length > 0
      ? cards.map((card) => contentfulComposePageToArticleCard(card))
      : []
  return (
    <BlogGrid>
      <ArticleRowTitle title={title} url={url} />
      <Spacer height={{ sm: "24px", md: "32px" }} />
      <ArticleCardGrid
        articles={articles}
        overflow={"carousel"}
        dotsHeight={DOTS_HEIGHT}
      />
      <Spacer
        height={{ sm: "32px", md: "48px", lg: `calc(48px - ${DOTS_HEIGHT})` }}
      />
    </BlogGrid>
  )
}

FeaturedArticles.Industry = ({
  title,
  cards,
}: Omit<IFeaturedArticlesProps, "url" | "website">) => {
  const blogCards: IBlogCard[] = cards
    ? cards.map((card) => ({
        fluidImage: card.content.image.fluid,
        title: card.title,
        description: truncateContent(
          richTextObjectToString(card.content.content)
        ),
        link: InternalRoutes.Blog(
          card.content.tags.category,
          card.content.tags.topic,
          card.slug
        ),
      }))
    : []

  return (
    <BlogCardGrid title={title} blogCardDetails={blogCards} divider={false} />
  )
}

export const FeaturedArticlesFragment = graphql`
  fragment ContentfulFeaturedArticles on ContentfulFeaturedCards {
    contentful_id
    title
    cards {
      title
      slug
      content {
        ...ContentfulBlogPostFragment
      }
    }
  }
`
export const FEATURED_ARTICLES_TYPENAME = "ContentfulFeaturedCards"
export default FeaturedArticles
